import React, { useState } from "react";

const styles = {
  container: {
    maxWidth: "820px",
    margin: "0 auto",
    padding: "0 16px",
    fontFamily: '"Poppins", sans-serif',
    color: "#1A1A1A",
    lineHeight: 1.72,
    fontSize: "15px",
  },
  tabBar: {
    display: "flex",
    gap: "0",
    borderBottom: "2px solid #E2E0DC",
    marginBottom: "32px",
  },
  tab: {
    flex: 1,
    textAlign: "center",
    padding: "12px 20px 14px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#888",
    background: "none",
    border: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    borderBottom: "2.5px solid transparent",
    marginBottom: "-2px",
    transition: "color 0.2s",
  },
  activeTab: {
    color: "#3A6ED4",
    borderBottomColor: "#3A6ED4",
  },
  heading: {
    fontSize: "20px",
    fontWeight: 700,
    marginTop: "40px",
    marginBottom: "12px",
  },
  paragraph: {
    marginBottom: "14px",
  },
};

const TermsOfUse = () => (
  <div>
    <h5 style={styles.heading}>
      <strong>1. Introduction</strong>
    </h5>
    <p style={styles.paragraph}>
      These Terms of Use ("Terms") are a legally binding agreement between you
      and workbrook LLC or, where applicable, the workbrook affiliate identified
      in these Terms ("workbrook," "we," "us," or "our"), and govern your access
      to and use of the workbrook website, mobile applications, web application,
      software, recruiter portal, referral platform, communications, and related
      services (collectively, the "Services").
    </p>
    <p style={styles.paragraph}>
      By accessing or using the Services, creating an account, posting a job,
      making a referral, applying for a job, engaging a recruiter, purchasing a
      paid service, or otherwise interacting with the platform, you agree to be
      bound by these Terms and the workbrook Privacy Policy. If you do not agree,
      you must not use the Services.
    </p>
    <p style={styles.paragraph}>
      If you use the Services on behalf of a company, employer, recruitment
      agency, or other legal entity, you represent and warrant that you have
      authority to bind that entity to these Terms. In that case, "you" and
      "your" refer to both you and that entity.
    </p>
    <p style={styles.paragraph}>
      You must be at least 16 years old to use the Services, or older if the
      laws of your jurisdiction require a higher minimum age. The English version
      of these Terms controls to the extent any translated version conflicts with
      it.
    </p>

    <h5 style={styles.heading}>
      <strong>2. Definitions</strong>
    </h5>
    <p style={styles.paragraph}>
      For purposes of these Terms: "Applicant" means a user who applies for a
      job through the Services. "Candidate" means an individual who is
      identified, referred, sourced, applies, interviews, or is considered for a
      role through the Services. "Employer" or "Job Poster" means a user or
      organization that posts a job, advertises an opportunity, or uses the
      Services to hire talent. "Recruiter" means a user approved by workbrook to
      offer recruiting or talent acquisition services through the recruiter
      portal. "Referrer" means a user who shares a job opportunity through
      workbrook and refers a potential Applicant.
    </p>
    <p style={styles.paragraph}>
      "Referral" means a tracked submission, share, recommendation, or
      platform-recognized introduction of a Candidate to a job through
      workbrook. "Hire" means an Applicant or Candidate beginning paid
      employment, consulting, contract work, or other compensated engagement
      with an Employer or its affiliate. "Start Date" means the first day on
      which the hired Candidate begins paid work in the applicable role.
      "Wallet" means a prepaid balance, employer credit balance, promotional
      credit balance, referral reward balance, or similar stored-value mechanism
      within the Services.
    </p>

    <h5 style={styles.heading}>
      <strong>3. Eligibility, Registration, and Accounts</strong>
    </h5>
    <p style={styles.paragraph}>
      To use certain features of the Services, you must create an account and
      provide accurate, current, and complete information. You agree to keep your
      information updated at all times and to maintain a valid email address that
      you own and control.
    </p>
    <p style={styles.paragraph}>
      You are responsible for maintaining the confidentiality of your account
      credentials and for all activity that occurs under your account. You must
      notify workbrook promptly if you believe your account or security
      credentials have been compromised or used without authorization.
    </p>
    <p style={styles.paragraph}>
      workbrook may permit sign-in through third-party services or social
      networking platforms. If you use such a login method, you authorize
      workbrook to receive information from that third party in accordance with
      your settings and permissions on that service.
    </p>
    <p style={styles.paragraph}>
      workbrook may reject, suspend, restrict, or terminate any account where we
      reasonably believe the account information is false, incomplete,
      unauthorized, duplicative, fraudulent, abusive, or otherwise inconsistent
      with these Terms.
    </p>

    <h5 style={styles.heading}>
      <strong>4. Use on Behalf of an Organization</strong>
    </h5>
    <p style={styles.paragraph}>
      If you use the Services on behalf of an employer, company, recruitment
      agency, or other legal entity, you represent and warrant that you are duly
      authorized to act on behalf of that entity, that the entity agrees to be
      bound by these Terms, and that you will use the Services only within the
      scope of your authority.
    </p>
    <p style={styles.paragraph}>
      Organizations are responsible for activity conducted through accounts they
      authorize or administer. If you falsely claim authority to act on behalf of
      an entity, workbrook may suspend or terminate your account and pursue any
      remedies available under law or contract.
    </p>

    <h5 style={styles.heading}>
      <strong>5. Intellectual Property and Limited License</strong>
    </h5>
    <p style={styles.paragraph}>
      The Services, including all software, design, layout, text, graphics,
      logos, trademarks, interface elements, platform features, databases, and
      other content made available by workbrook, are owned by or licensed to
      workbrook and are protected by intellectual property and other applicable
      laws.
    </p>
    <p style={styles.paragraph}>
      Subject to your compliance with these Terms, workbrook grants you a
      limited, revocable, non-exclusive, non-transferable, non-sublicensable
      license to access and use the Services for their intended purposes.
    </p>
    <p style={styles.paragraph}>
      Except as expressly permitted by workbrook in writing, you may not copy,
      reproduce, republish, distribute, sell, license, rent, lease, transfer, or
      commercially exploit the Services or workbrook content; modify, adapt,
      translate, create derivative works from, decompile, reverse engineer, or
      disassemble the Services; scrape, extract, harvest, index, or use data
      from the Services for dataset creation, model training, or any
      unauthorized commercial purpose; or remove, alter, or obscure any
      copyright, trademark, or proprietary notices.
    </p>

    <h5 style={styles.heading}>
      <strong>6. User Content and License to workbrook</strong>
    </h5>
    <p style={styles.paragraph}>
      You retain ownership of content, information, resumes, profiles, job
      descriptions, images, messages, application materials, and other materials
      that you upload, post, submit, transmit, or otherwise make available
      through the Services ("User Content"), subject to the rights you grant in
      these Terms.
    </p>
    <p style={styles.paragraph}>
      By submitting User Content through the Services, you grant workbrook a
      non-exclusive, worldwide, royalty-free license to host, store, reproduce,
      process, format, display, distribute, transmit, analyze, adapt, and use
      that User Content only as reasonably necessary to operate, provide, secure,
      maintain, improve, and support the Services; facilitate hiring, referrals,
      recruiting, communication, and related platform functions; generate
      analytics in aggregated or de-identified form; enforce these Terms; and
      comply with legal obligations.
    </p>
    <p style={styles.paragraph}>
      You represent and warrant that you own or control all rights necessary to
      submit the User Content, that the User Content does not violate law or
      infringe the rights of any third party, and that you have any required
      consents, permissions, or lawful basis to share personal information
      contained in the User Content.
    </p>

    <h5 style={styles.heading}>
      <strong>7. Acceptable Use</strong>
    </h5>
    <p style={styles.paragraph}>
      You agree to use the Services only for lawful, authorized, and intended
      purposes. You are solely responsible for your use of the Services, your
      account, your User Content, and your interactions with other users.
    </p>
    <p style={styles.paragraph}>
      You may not upload, post, send, transmit, or otherwise make available
      content or information that is unlawful, fraudulent, deceptive, misleading,
      abusive, threatening, defamatory, discriminatory, obscene, sexually
      explicit, infringing, or otherwise harmful. You may not use the Services in
      a way that violates privacy rights, confidentiality obligations, anti-spam
      laws, employment laws, anti-discrimination laws, data protection laws,
      intellectual property rights, or any other applicable law.
    </p>

    <h5 style={styles.heading}>
      <strong>8. Prohibited Activities</strong>
    </h5>
    <p style={styles.paragraph}>
      You agree not to create false accounts, duplicate accounts, or accounts
      under false pretenses; impersonate any person or entity, or misrepresent
      your identity, authority, qualifications, work history, or affiliation;
      post fake jobs, fake candidates, fake applications, fake referrals, or
      misleading hiring activity; or collude with another user, Employer,
      Applicant, Recruiter, or Candidate to manipulate payouts, rankings, Match
      Scores, billing events, or platform outcomes.
    </p>
    <p style={styles.paragraph}>
      You further agree not to use bots, scripts, crawlers, spiders, scrapers,
      data-mining tools, or other automated means to access or interact with the
      Services without workbrook's written authorization; attempt to bypass
      security, authentication, access controls, or encryption; upload viruses,
      malware, or other harmful code; interfere with or disrupt the Services;
      access non-public areas of the Services; or use platform data or output to
      train artificial intelligence or machine learning systems without
      authorization.
    </p>

    <h5 style={styles.heading}>
      <strong>9. Applicant Terms</strong>
    </h5>
    <p style={styles.paragraph}>
      Applicants may apply to jobs through the Services using their workbrook
      accounts, subject to job eligibility requirements and these Terms.
    </p>
    <p style={styles.paragraph}>
      By applying to a job through workbrook, you authorize workbrook to transmit
      your application materials, profile information, resume, and related
      information to the relevant Employer, Job Poster, Recruiter, or other
      authorized hiring participant involved in the opportunity; represent and
      warrant that the information you provide is truthful, current, and not
      misleading; and acknowledge that Employers, not workbrook, make hiring
      decisions.
    </p>
    <p style={styles.paragraph}>
      Applicants may withdraw from a job application through available platform
      tools or by otherwise notifying the relevant party, subject to ongoing
      legal, compliance, or record-retention requirements.
    </p>

    <h5 style={styles.heading}>
      <strong>10. Recruiter Terms</strong>
    </h5>
    <p style={styles.paragraph}>
      To act as a Recruiter on workbrook, you must create a recruiter profile and
      satisfy workbrook's eligibility requirements, including any experience,
      screening, approval, or profile-completion requirements communicated by
      workbrook.
    </p>
    <p style={styles.paragraph}>
      Recruiters must use the Services only for legitimate and lawful recruiting
      purposes and remain subject to these Terms. Where workbrook provides a
      separate Recruiter Agreement or service-specific contract, that agreement
      supplements these Terms and controls in the event of conflict as to
      Recruiter services.
    </p>

    <h5 style={styles.heading}>
      <strong>11. Privacy, Communications, and Data Protection</strong>
    </h5>
    <p style={styles.paragraph}>
      Your use of the Services is subject to workbrook's Privacy Policy, which
      describes how workbrook collects, uses, stores, discloses, and otherwise
      processes personal data.
    </p>
    <p style={styles.paragraph}>
      You agree not to upload, share, transmit, or otherwise disclose personal
      information through the Services unless you have the legal right or lawful
      basis to do so. Employers, Recruiters, and Referrers are independently
      responsible for their own lawful handling of personal information they
      receive through the Services.
    </p>
    <p style={styles.paragraph}>
      workbrook may send service-related, transactional, operational, security,
      billing, legal, and support communications in connection with the Services.
      Marketing communications are subject to applicable law and any preferences
      or consents required by law.
    </p>

    <h5 style={styles.heading}>
      <strong>12. AI and Automated Features</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook may use artificial intelligence, machine learning, automation,
      algorithmic ranking, predictive systems, or similar technologies in
      connection with the Services, including to summarize job descriptions or
      candidate profiles, calculate Match Scores or relevance signals, rank or
      prioritize candidates or jobs, detect suspicious activity or fraud,
      recommend opportunities or candidates, and support outreach or workflow
      automation.
    </p>
    <p style={styles.paragraph}>
      These outputs are assistive tools only. They may be incomplete, inaccurate,
      probabilistic, or based on limited inputs. They do not constitute legal,
      HR, employment, or professional advice, and they do not replace human
      judgment. Users remain solely responsible for decisions they make using the
      Services.
    </p>

    <h5 style={styles.heading}>
      <strong>13. Service Management and Enforcement</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook reserves the right, but not the obligation, to monitor the
      Services for compliance with these Terms; investigate suspected violations,
      fraud, abuse, billing disputes, and suspicious activity; remove, disable,
      restrict, or refuse access to content, jobs, features, accounts, Wallets,
      or Services; and preserve, review, and disclose information as reasonably
      necessary to enforce these Terms, comply with law, or protect rights,
      property, and safety.
    </p>
    <p style={styles.paragraph}>
      workbrook's records, logs, timestamps, and platform data may be used in
      determining attribution, billing, fraud, reward eligibility, and compliance
      outcomes.
    </p>

    <h5 style={styles.heading}>
      <strong>14. Suspension and Termination</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook may suspend, limit, or terminate your access to all or part of
      the Services immediately where reasonably necessary due to suspected fraud,
      abuse, manipulation, security risk, unlawful conduct, nonpayment,
      violation of these Terms, false or misleading account or transaction
      information, or legal or reputational risk to workbrook or the Services.
    </p>
    <p style={styles.paragraph}>
      Where reasonably practicable, workbrook may provide notice of suspension or
      termination, but workbrook is not required to do so in advance where urgent
      action is needed.
    </p>
    <p style={styles.paragraph}>
      Account deletion or discontinuation does not relieve you of obligations
      accrued before termination, including payment obligations, tax obligations,
      indemnity obligations, confidentiality obligations, or disputes already in
      progress.
    </p>

    <h5 style={styles.heading}>
      <strong>15. Disclaimers</strong>
    </h5>
    <p style={styles.paragraph}>
      THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE
      MAXIMUM EXTENT PERMITTED BY LAW, WORKBROOK DISCLAIMS ALL WARRANTIES,
      WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING IMPLIED
      WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
      NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING OR
      USAGE OF TRADE.
    </p>
    <p style={styles.paragraph}>
      workbrook does not warrant that the Services will be uninterrupted,
      error-free, secure, or always available; that any job posting, referral,
      applicant profile, resume, recruiter profile, company information, salary
      information, Match Score, or platform content is accurate, complete,
      reliable, or lawful; or that any hire, interview, application, reward, or
      commercial outcome will occur.
    </p>
    <p style={styles.paragraph}>
      Users are solely responsible for conducting their own due diligence,
      screening, interviews, reference checks, background checks, legal
      compliance, and business judgment.
    </p>

    <h5 style={styles.heading}>
      <strong>16. Limitation of Liability</strong>
    </h5>
    <p style={styles.paragraph}>
      To the maximum extent permitted by law, workbrook and its affiliates,
      officers, directors, employees, contractors, licensors, and agents will not
      be liable for any indirect, incidental, consequential, special, exemplary,
      or punitive damages, including loss of profits, loss of revenue, loss of
      opportunity, loss of data, business interruption, reputational harm, or
      procurement of substitute services, arising out of or relating to the
      Services or these Terms, even if advised of the possibility of such
      damages.
    </p>
    <p style={styles.paragraph}>
      To the maximum extent permitted by law, workbrook's aggregate liability for
      all claims arising out of or relating to the Services or these Terms will
      not exceed the greater of the amount you paid to workbrook in the 12 months
      preceding the event giving rise to the claim or USD 100. Nothing in these
      Terms excludes liability that cannot lawfully be excluded or limited.
    </p>

    <h5 style={styles.heading}>
      <strong>17. Indemnification</strong>
    </h5>
    <p style={styles.paragraph}>
      You agree to defend, indemnify, and hold harmless workbrook, its
      affiliates, and their respective officers, directors, employees,
      contractors, licensors, and agents from and against any claims,
      liabilities, damages, judgments, losses, costs, and expenses, including
      reasonable attorneys' fees, arising out of or related to your use of the
      Services, your violation of these Terms, your User Content, your violation
      of any law or the rights of a third party, your misuse of personal
      information or candidate data, or any fraud, misrepresentation, spam,
      discriminatory hiring activity, fake postings, fake referrals, or other
      prohibited conduct by you.
    </p>

    <h5 style={styles.heading}>
      <strong>18. Modifications, Interruptions, and Service Changes</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook may modify, suspend, discontinue, or update any aspect of the
      Services at any time, including features, pricing, tools, algorithms,
      integrations, tiers, reward structures, or availability. workbrook is not
      liable for any unavailability, downtime, interruption, maintenance event,
      feature withdrawal, pricing change, or service change, except as required
      by law or expressly agreed in writing.
    </p>
    <p style={styles.paragraph}>
      workbrook may make changes to these Terms from time to time. If we make
      material changes, we may provide notice through the Services, by email, or
      by other reasonable means. Your continued use of the Services after the
      effective date of updated Terms constitutes acceptance of the updated
      Terms.
    </p>
    <p style={styles.paragraph}>
      Beta Features. workbrook may designate certain features, tools, workflows,
      integrations, AI functions, dashboards, or services as beta, pilot,
      early-access, preview, or test features ("Beta Features"). Beta Features
      may be incomplete, unstable, unavailable, or subject to errors,
      interruptions, data loss, or material change. workbrook may modify,
      suspend, or discontinue Beta Features at any time, with or without notice,
      and has no obligation to support, maintain, correct, or continue Beta
      Features. Beta Features are provided on an "as is" and "as available"
      basis, and users use them at their own risk.
    </p>

    <h5 style={styles.heading}>
      <strong>19. Dispute Resolution and Governing Law</strong>
    </h5>
    <p style={styles.paragraph}>
      These Terms are governed by the laws of the State of Texas, without regard
      to conflict of laws principles. Before filing a formal legal claim, each
      party agrees to first attempt to resolve the dispute informally by written
      notice to the other party describing the nature of the dispute and the
      requested relief.
    </p>
    <p style={styles.paragraph}>
      If the dispute is not resolved informally within 30 days, either party may
      require the dispute to proceed to mediation in Texas, unless emergency
      injunctive relief is needed. If mediation does not resolve the dispute,
      then, unless applicable law requires otherwise, the dispute shall be
      brought exclusively in the state or federal courts located in Texas, and
      each party consents to the personal jurisdiction and venue of those courts.
    </p>
    <p style={styles.paragraph}>
      Class Action and Jury Trial Waiver: You and workbrook agree that any
      dispute resolution proceedings will be conducted only on an individual
      basis and not in a class, consolidated, or representative action. If for
      any reason a claim proceeds in court rather than in arbitration or
      mediation, you and workbrook each waive any right to a jury trial.
    </p>

    <h5 style={styles.heading}>
      <strong>20. Miscellaneous</strong>
    </h5>
    <p style={styles.paragraph}>
      These Terms, together with any policies, pricing pages, order forms,
      service-specific agreements, and other terms expressly incorporated by
      reference, constitute the entire agreement between you and workbrook
      regarding the Services, except where a separate written agreement governs a
      specific service.
    </p>
    <p style={styles.paragraph}>
      If any provision of these Terms is found unenforceable, the remaining
      provisions will remain in full force and effect. workbrook's failure to
      enforce any provision is not a waiver of its right to do so later. You may
      not assign or transfer these Terms without workbrook's prior written
      consent. workbrook may assign these Terms in connection with a merger,
      acquisition, reorganization, sale of assets, or by operation of law.
    </p>
    <p style={styles.paragraph}>
      For legal notices or questions regarding these Terms, contact:
      info@workbrook.com.
    </p>
  </div>
);

const EmployerTerms = () => (
  <div>
    <h5 style={styles.heading}>
      <strong>1. Scope and Relationship to Other Documents</strong>
    </h5>
    <p style={styles.paragraph}>
      These Employer / Marketplace Commercial Terms ("Employer Commercial Terms")
      govern an Employer's use of workbrook's paid employer-facing marketplace
      and hiring services, including tiered job promotion, Wallet funding,
      usage-based billing, placement services, guarantees, and related commercial
      mechanics.
    </p>
    <p style={styles.paragraph}>
      These Employer Commercial Terms are incorporated into and form part of the
      workbrook Terms of Use. In the event of conflict, these Employer Commercial
      Terms control for the commercial subjects they expressly govern. Separate
      signed proposals, order forms, search agreements, or client agreements
      control to the extent they expressly override these Employer Commercial
      Terms.
    </p>

    <h5 style={styles.heading}>
      <strong>2. Employer Eligibility and Authority</strong>
    </h5>
    <p style={styles.paragraph}>
      By posting a job or purchasing any paid employer service, you represent and
      warrant that the job is real, lawful, and currently open or reasonably
      anticipated to open; that you are authorized to recruit for the role; that
      the job description, company information, salary information, and hiring
      details are accurate in all material respects; and that your use of
      workbrook complies with applicable employment, anti-discrimination, and
      privacy laws.
    </p>
    <p style={styles.paragraph}>
      You may not post speculative, misleading, already-filled, unlawful,
      discriminatory, or confidential jobs.
    </p>

    <h5 style={styles.heading}>
      <strong>3. Employer Conduct and Reporting Obligations</strong>
    </h5>
    <p style={styles.paragraph}>
      Employers must keep job postings current and accurate; promptly mark roles
      as filled, paused, canceled, or closed; provide materially accurate
      information about job requirements, compensation, location, reporting
      structure, and work authorization requirements; use Candidate information
      only for legitimate hiring-related purposes; and comply with all applicable
      laws in reviewing, contacting, interviewing, and hiring Candidates.
    </p>
    <p style={styles.paragraph}>
      Employers must also provide reasonably timely and truthful updates
      regarding interviews, offers, acceptances, Start Dates, compensation, and
      terminations where required for billing, referral rewards, replacement
      guarantees, audit, or dispute resolution.
    </p>

    <h5 style={styles.heading}>
      <strong>4. Candidate Attribution and Ownership</strong>
    </h5>
    <p style={styles.paragraph}>
      A Candidate is considered introduced by workbrook when workbrook, a
      Referrer through workbrook, a Recruiter through workbrook, or any
      workbrook-supported process first provides or tracks the Candidate in
      connection with a role.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise agreed in writing, workbrook's attribution period for
      introduced or referred Candidates is 12 months from the most recent
      material workbrook-tracked activity involving that Candidate and the
      Employer. If an Employer hires, engages, or causes a Candidate to be hired
      or engaged directly or indirectly during the attribution period, workbrook
      may assess the applicable fee or treat the hire as reward-eligible under
      the applicable workbrook terms.
    </p>
    <p style={styles.paragraph}>
      For the purposes of this Section, 'material workbrook-tracked activity' is
      strictly defined as one or more of the following verifiable platform
      events: (a) a formal job application submitted by the Candidate, (b) a
      documented interview scheduled or facilitated via the Services, or (c)
      direct, mutual written correspondence between the Employer and the
      Candidate initiated through the platform. This clause is associated with
      Tier 3 and/or Recruiter placement services
    </p>

    <h5 style={styles.heading}>
      <strong>5. Tier 1 - Direct-to-Talent</strong>
    </h5>
    <p style={styles.paragraph}>
      Tier 1 provides standard visibility in the workbrook job feed and may
      include basic posting functionality. Unless workbrook states otherwise,
      Tier 1 does not include premium ranking, white-glove search, AI-assisted
      outreach, or pay-per-view referral amplification.
    </p>
    <p style={styles.paragraph}>
      workbrook may impose fair-use limits on free job postings, active jobs,
      editing frequency, refreshes, visibility boosts, or other free-tier
      features.
    </p>

    <h5 style={styles.heading}>
      <strong>6. Tier 2 - Community Boosted</strong>
    </h5>
    <p style={styles.paragraph}>
      Tier 2 is a usage-based service in which an Employer funds a Wallet and is
      charged a stated View Rate or other defined usage rate for qualifying
      access events generated through referral or premium discovery mechanisms.
    </p>
    <p style={styles.paragraph}>
      A billable View is determined by workbrook's tracking systems and may
      exclude duplicate views by the same user within a defined period, bot
      traffic, fraudulent or invalid activity, accidental or incomplete loads,
      and any event workbrook determines should not be charged in its discretion.
    </p>
    <p style={styles.paragraph}>
      workbrook may allow Employers to set daily or total spend caps. Once a cap
      or available Wallet balance is exhausted, workbrook may pause, reduce, or
      revert the job's premium exposure until funding is restored or settings are
      changed. Except where required by law or expressly stated by workbrook,
      valid usage charges are non-refundable once incurred.
    </p>

    <h5 style={styles.heading}>
      <strong>7. Tier 3 - Full Placement / Performance-Based Search</strong>
    </h5>
    <p style={styles.paragraph}>
      Tier 3 is a premium, performance-based hiring service that may include
      targeted sourcing, referral bounty activation, AI-assisted talent discovery
      or passive outreach, priority screening or ranking, and white-glove
      support.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise agreed in writing, activation of Tier 3 may require a
      non-refundable retainer. The remaining fee structure, placement
      percentages, role bands, and invoicing triggers are as stated in the
      applicable order form, proposal, pricing page, or service-specific
      agreement.
    </p>
    <p style={styles.paragraph}>
      If Tier 3 is governed by a separate client agreement or search agreement,
      that agreement controls to the extent of any conflict.
    </p>

    <h5 style={styles.heading}>
      <strong>8. Replacement Guarantee</strong>
    </h5>
    <p style={styles.paragraph}>
      Where workbrook offers a replacement guarantee for an applicable paid
      search or placement service, workbrook may conduct one replacement search
      at no additional professional fee if the placed Candidate leaves or is
      terminated for cause during the Guarantee Period, provided that the
      Employer notifies workbrook in writing within the required notice window;
      the termination did not result from redundancy, material role change,
      compensation change, relocation, restructuring, or employer misconduct;
      all applicable fees have been paid in full; and the Employer otherwise
      remains in compliance with the workbrook documents.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise stated, replacement guarantees do not automatically
      include new referral bounties, paid advertising costs, or third-party
      expenses.
    </p>

    <h5 style={styles.heading}>
      <strong>9. Wallets and Credits</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook may offer Wallet functionality for prepaid balances, purchased
      credits, referral rewards, promotional credits, or similar value units.
      Unless workbrook states otherwise, purchased Wallet balances are
      non-interest-bearing, promotional credits have no cash value and may expire
      or be withdrawn, Wallet balances are non-transferable except as expressly
      permitted by workbrook, and workbrook may distinguish between purchased
      credits and promotional credits for redemption order, refund treatment, or
      expiration.
    </p>
    <p style={styles.paragraph}>
      Employers are responsible for maintaining sufficient Wallet balance where a
      service requires prepaid funding.
    </p>

    <h5 style={styles.heading}>
      <strong>10. Billing, Invoicing, and Payment</strong>
    </h5>
    <p style={styles.paragraph}>
      You authorize workbrook and its payment processors to charge your selected
      payment method for fees you incur. You must provide accurate billing, tax,
      and payment information and keep that information current. You are
      responsible for any fees charged by your payment provider, bank, card
      issuer, or payment processor.
    </p>
    <p style={styles.paragraph}>
      Except where required by law or expressly stated by workbrook in writing,
      fees already incurred for valid Services, usage events, retainers, consumed
      credits, subscriptions already billed, and completed service milestones are
      non-refundable.
    </p>
    <p style={styles.paragraph}>
      If a payment fails, is reversed, is charged back, or is disputed,
      workbrook may suspend your access to paid Services, recover or offset
      amounts owed, invalidate related rewards or promotional benefits where
      appropriate, and pursue collection costs and legal remedies.
    </p>

    <h5 style={styles.heading}>
      <strong>11. Taxes</strong>
    </h5>
    <p style={styles.paragraph}>
      Employers are responsible for all taxes, duties, levies, withholdings, and
      other governmental charges arising from their use of paid Services, except
      taxes imposed on workbrook's net income. workbrook may withhold amounts
      where required by law.
    </p>

    <h5 style={styles.heading}>
      <strong>12. Anti-Circumvention</strong>
    </h5>
    <p style={styles.paragraph}>
      Employers may not bypass workbrook to avoid applicable fees, billing rules,
      or referral reward obligations. Circumvention includes hiring or engaging a
      workbrook-introduced or workbrook-referred Candidate off-platform to avoid
      fees; requesting that Applicants, Candidates, Referrers, or Recruiters
      continue outside workbrook for fee-avoidance purposes; and failing to
      report a hire or material hiring event connected to a workbrook-generated
      Candidate.
    </p>
    <p style={styles.paragraph}>
      Where workbrook reasonably determines circumvention has occurred, workbrook
      may charge the applicable placement fee or marketplace fee, deny or
      recover promotional benefits, suspend or terminate the Employer account,
      withhold services, and pursue additional remedies available under law or
      contract.
    </p>

    <h5 style={styles.heading}>
      <strong>13. Audits, Disputes, and workbrook Records</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook may use its records, logs, timestamps, analytics, billing data,
      traffic filters, and platform data to determine usage-based billing,
      attribution, circumvention, reward eligibility, and compliance outcomes.
    </p>
    <p style={styles.paragraph}>
      Employers agree to cooperate in good faith with reasonable requests for
      confirmation of interviews, offers, salary, Start Date, and termination
      data relevant to billing, rewards, or guarantees.
    </p>

    <h5 style={styles.heading}>
      <strong>14. Governing Terms</strong>
    </h5>
    <p style={styles.paragraph}>
      All disclaimers, liability limitations, indemnities, dispute-resolution
      terms, and miscellaneous provisions in the workbrook Terms of Use apply to
      these Employer Commercial Terms unless a separate signed agreement
      expressly states otherwise.
    </p>
  </div>
);

const ReferralTerms = () => (
  <div>
    <h5 style={styles.heading}>
      <strong>1. Scope and Relationship to Other Documents</strong>
    </h5>
    <p style={styles.paragraph}>
      These Referral Rewards Terms ("Referral Terms") govern Referral validity,
      attribution, reward eligibility, payout timing, fraud controls, taxes, and
      disputes for any Referral Reward or bounty program made available through
      workbrook.
    </p>
    <p style={styles.paragraph}>
      These Referral Terms are incorporated into and form part of the workbrook
      Terms of Use. In the event of conflict, these Referral Terms control for
      the reward subjects they expressly govern. Job-specific reward notices,
      campaign terms, or employer-specific reward programs may supplement these
      Referral Terms and will control to the extent they expressly vary the
      general program rules.
    </p>

    <h5 style={styles.heading}>
      <strong>2. Referrer Eligibility</strong>
    </h5>
    <p style={styles.paragraph}>
      Any eligible user may make a Referral through the Services, subject to
      these Terms and any role, geography, campaign, or job-specific restrictions
      stated by workbrook.
    </p>
    <p style={styles.paragraph}>
      By making a Referral, you represent and warrant that you are acting
      lawfully and in good faith; that you have a reasonable basis to believe the
      referred person may be relevant to the role; that you have any required
      permission or lawful basis to share the referred person's information or to
      contact them in connection with the opportunity; and that you will not
      engage in spam, mass low-quality submissions, fake referrals,
      self-referrals, or manipulation.
    </p>

    <h5 style={styles.heading}>
      <strong>3. When a Referral Is Credited</strong>
    </h5>
    <p style={styles.paragraph}>
      A Referral is credited when workbrook's systems determine that a Candidate
      was referred through your unique workbrook-tracked referral mechanism and
      the referral satisfies workbrook's attribution rules.
    </p>
    <p style={styles.paragraph}>
      A Referral may be deemed invalid if the Candidate was already in the
      Employer's active pipeline; the Candidate had already applied or been
      submitted for the role within the relevant lookback period; the referral
      cannot be verified; the referral resulted from prohibited conduct; or the
      referral is duplicated and another Referrer has valid prior timestamp
      priority.
    </p>
    <p style={styles.paragraph}>
      Unless workbrook states otherwise for a campaign or service, duplicate
      referrals are resolved on a first valid submission basis using workbrook's
      records. In the event of duplicate referrals for the exact same Candidate
      and role, the Referral Reward will be exclusively credited to the Referrer
      whose unique tracking link generated the earliest timestamped application
      or initial account creation, as recorded down to the millisecond by
      workbrook's internal server logs.
    </p>

    <h5 style={styles.heading}>
      <strong>4. Successful Referral and Reward Trigger</strong>
    </h5>
    <p style={styles.paragraph}>
      A Referral Reward is earned only if the referred Candidate becomes a
      verified Hire for a Tier 3 job, the Hire is attributable to your Referral
      under workbrook's systems and rules, any applicable waiting period,
      Guarantee Period, fraud checks, employer confirmation steps, and payment
      conditions are satisfied, and you remain in compliance with the workbrook
      documents.
    </p>
    <p style={styles.paragraph}>
      Unless otherwise stated by workbrook, a referral is not reward-eligible
      merely because a Candidate clicks, views, applies, interviews, or receives
      an offer.
    </p>

    <h5 style={styles.heading}>
      <strong>5. Reward Amounts</strong>
    </h5>
    <p style={styles.paragraph}>
      Reward percentages, fixed amounts, role bands, payout schedules, and
      promotional reward structures may vary by job, campaign, geography, tier,
      or Employer arrangement. Where workbrook publishes a specific reward
      formula for a role or campaign, that published formula applies.
    </p>
    <p style={styles.paragraph}>
      Unless explicitly included, bonuses, equity, relocation packages, and
      non-cash benefits are excluded from salary-based reward calculations.
      workbrook may round, adjust, or recalculate rewards to reflect verified
      salary information, corrected data, currency conversion, taxes, caps,
      minimums, maximums, or employer-specific terms.
    </p>

    <h5 style={styles.heading}>
      <strong>6. Payout Timing and Conditions</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook may pay rewards in stages, including milestone-based structures
      such as partial payment after 30 days of employment and the balance after
      completion of the Guarantee Period.
    </p>
    <p style={styles.paragraph}>
      Before payout, workbrook may require identity verification, payment account
      verification, tax documentation, fraud screening, employer confirmation of
      salary, Start Date, and role details, and confirmation that the Hire
      remains in good standing through the relevant milestone date.
    </p>
    <p style={styles.paragraph}>
      workbrook may delay, withhold, reverse, reduce, or deny payout where
      fraud, abuse, collusion, or manipulation is suspected; the Employer
      disputes attribution in good faith; the Employer has not paid workbrook
      where Employer payment is a condition of the reward model; required tax or
      verification information has not been provided; the Candidate did not
      satisfy the required employment milestone; or workbrook determines the
      Referral is invalid under these Terms.
    </p>

    <h5 style={styles.heading}>
      <strong>7. Referrer Conduct Restrictions</strong>
    </h5>
    <p style={styles.paragraph}>
      Referrers may not refer themselves; offer kickbacks, side-payments, or
      reward-sharing arrangements to Candidates, hiring managers, recruiters, or
      Employer representatives for the purpose of manipulating outcomes; submit
      Candidates without authorization where such authorization is required by
      law; create multiple accounts to increase reward eligibility; or manipulate
      referral timestamps, links, identities, or platform behavior.
    </p>
    <p style={styles.paragraph}>
      workbrook may suspend referral privileges, invalidate Referrals, reverse
      rewards, or terminate accounts for any violation of this Section.
    </p>

    <h5 style={styles.heading}>
      <strong>8. Taxes and Compliance</strong>
    </h5>
    <p style={styles.paragraph}>
      Referrers are solely responsible for any taxes, duties, levies,
      withholdings, or reporting obligations arising from any Referral Reward,
      except to the extent workbrook is required by law to withhold or report
      amounts.
    </p>
    <p style={styles.paragraph}>
      workbrook may require completion of tax forms, identity verification,
      payment verification, or compliance checks before releasing a reward and
      may withhold or report amounts where required by law.
    </p>

    <h5 style={styles.heading}>
      <strong>9. Employer Nonpayment and Dependency Risks</strong>
    </h5>
    <p style={styles.paragraph}>
      Where a reward program is funded or economically dependent on an Employer
      payment obligation, workbrook may condition reward payout on workbrook's
      receipt of the corresponding Employer payment. If an Employer fails to pay,
      becomes insolvent, disputes attribution in good faith, or otherwise
      prevents completion of the funding event, workbrook may delay, reduce, or
      decline payout unless workbrook expressly agrees otherwise in writing.
    </p>
    <p style={styles.paragraph}>
      Referrers acknowledge and agree that workbrook's obligation to issue a
      Referral Reward is strictly contingent upon workbrook's actual receipt of
      the corresponding placement fee from the Employer. While workbrook will
      make commercially reasonable efforts to collect owed fees, Employer
      default, insolvency, or breach of contract may result in the delay or
      cancellation of the Referral Reward without liability to workbrook
    </p>

    <h5 style={styles.heading}>
      <strong>10. Disputes and Final Determinations</strong>
    </h5>
    <p style={styles.paragraph}>
      workbrook may use its records, timestamps, link tracking, platform events,
      billing data, communications logs, and other platform evidence to determine
      Referral validity, duplicate priority, reward eligibility, fraud, and
      payout amount.
    </p>
    <p style={styles.paragraph}>
      In the event of a dispute about a Referral, duplicate claim, attribution,
      or reward amount, workbrook's good-faith determination based on its records
      will control unless a separate written agreement or applicable law requires
      otherwise.
    </p>

    <h5 style={styles.heading}>
      <strong>11. Governing Terms</strong>
    </h5>
    <p style={styles.paragraph}>
      All disclaimers, liability limitations, indemnities, dispute-resolution
      terms, and miscellaneous provisions in the workbrook Terms of Use apply to
      these Referral Terms unless a separate written agreement expressly states
      otherwise.
    </p>
  </div>
);

const TABS = [
  { key: "tou", label: "Terms of Use", Component: TermsOfUse },
  { key: "employer", label: "Employer / Marketplace", Component: EmployerTerms },
  { key: "referral", label: "Referral Rewards", Component: ReferralTerms },
];

const TermsAndConditions = () => {
  const [activeTab, setActiveTab] = useState("tou");

  const ActiveComponent = TABS.find((t) => t.key === activeTab)?.Component || TermsOfUse;

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 600px) {
          .wb-tab-bar {
            flex-direction: column !important;
            border-bottom: none !important;
            gap: 0 !important;
          }
          .wb-tab-bar button {
            border-bottom: 1px solid #E2E0DC !important;
            text-align: left !important;
          }
        }
      `}</style>
      <div className="wb-tab-bar" style={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.activeTab : {}),
            }}
            onClick={() => {
              setActiveTab(tab.key);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <ActiveComponent />
    </div>
  );
};

TermsAndConditions.propTypes = {};
TermsAndConditions.defaultProps = {};

export default TermsAndConditions;